import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { generatePkce } from "@/lib/pkce";
import { DERIV_AFFILIATE_URL } from "@/lib/socialLinks";

const ConfigContext = createContext({ config: null, loading: true });

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .get("/config")
      .then((res) => {
        if (active) setConfig(res.data);
      })
      .catch(() => {
        if (active)
          setConfig({
            site_name: "VTS",
            site_full_name: "VTS — Forex · Gold · Crypto Trading Desk",
            deriv_app_id: "1089",
            deriv_oauth_url: "https://oauth.deriv.com/oauth2/authorize",
            deriv_auth_url: "https://auth.deriv.com/oauth2/auth",
            deriv_ws_url: "wss://api.derivws.com/trading/v1/options/ws/public",
            affiliate_token: "",
            signup_url: DERIV_AFFILIATE_URL,
            markup_pct: 2.5,
          });
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  return (
    <ConfigContext.Provider value={{ config, loading }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => useContext(ConfigContext);

export const buildOAuthUrl = async (config, redirectUri, mode = "login") => {
  // Deriv's official PKCE OAuth flow at auth.deriv.com
  // (This is the ONLY supported OAuth flow per developers.deriv.com/llms.txt — the
  // legacy oauth.deriv.com/oauth2/authorize endpoint no longer works for new apps.)
  const { challenge, state } = await generatePkce();
  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.deriv_oauth_client_id || config.deriv_app_id,
    redirect_uri: redirectUri,
    scope: "trade",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  if (mode === "signup") {
    params.set("prompt", "registration");
    if (config.affiliate_token) {
      params.set("utm_source", config.affiliate_token);
      params.set("utm_medium", "affiliate");
      params.set("utm_campaign", "vts_signup");
    }
  }
  return `${config.deriv_auth_url}?${params.toString()}`;
};

// Primary CTA for marketing pages — ALWAYS the partner affiliate tracking link
// (frontend constant is the source of truth so a stale backend config can
// never serve a non-affiliate signup URL). Deriv attributes commission to you.
export const buildSignupUrl = () => {
  return DERIV_AFFILIATE_URL;
};
